import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import {
    MoreActionsWrapper,
    MoreIconButton,
    MoreIconWrapper,
    ActionsDropdownWrapper,
    Action,
} from './MoreActions.style';
import { Popup } from '../popup/Popup';
import { DeletePopup } from '../popup/popups/cms/DeletePopup';

class MoreActionsState {
    private readonly onEdit: (() => void) | undefined;
    private readonly onDuplicate: (() => void) | undefined;

    @observable public isDropdownShown: boolean;
    @observable public refElement: HTMLElement | null = null;
    @observable public inset: HTMLElement | null = null;
    @observable public defaultHeight: number = 32;

    constructor(onEdit: (() => void) | undefined, onDuplicate: (() => void) | undefined) {
        this.isDropdownShown = false;
        this.onEdit = onEdit;
        this.onDuplicate = onDuplicate;
        makeAutoObservable(this);
    }

    @action public setDropdown(value: boolean): void {
        this.isDropdownShown = value;
    }

    public setRef = (refElement: HTMLElement | null): void => {
        this.refElement = refElement;
    };

    @computed get refPositionData(): DOMRect | undefined {
        return this.refElement?.getBoundingClientRect();
    }

    @computed get dropdownInset(): string | undefined {
        if (this.refPositionData === undefined) {
            return;
        }

        const top = Math.floor(this.refPositionData.top);
        const left = Math.floor(this.refPositionData.left);

        return `${top + 25}px auto auto ${left - 80}px`;
    }

    @computed get dropdownHeight(): string {
        this.defaultHeight = 32;

        if (this.onEdit !== undefined) {
            this.defaultHeight = this.defaultHeight + 32;
        }

        if (this.onDuplicate !== undefined) {
            this.defaultHeight = this.defaultHeight + 32;
        }

        return this.defaultHeight.toString();
    }
}

interface MoreActionsPropsType {
    subsection?: 'one' | 'two';
    onEdit?: () => void;
    onDuplicate?: () => void;
    onDelete: () => void;
    handleHideDeletePopup?: () => void;
    handleShowDeletePopup?: () => void;
    showDeletePopup?: boolean;
}

export const MoreActions = observer('MoreActions', (props: MoreActionsPropsType) => {
    const { subsection, onEdit, onDuplicate, onDelete, handleHideDeletePopup, handleShowDeletePopup, showDeletePopup } =
        props;

    const [state] = useState(() => new MoreActionsState(onEdit, onDuplicate));

    const noDeletePopup =
        handleShowDeletePopup === undefined || handleHideDeletePopup === undefined || showDeletePopup === undefined;

    return (
        <MoreActionsWrapper
            onMouseEnter={(): void => state.setDropdown(true)}
            onMouseLeave={(): void => state.setDropdown(false)}
        >
            <MoreIconButton
                ref={state.setRef}
                data-test='more-actions'
            >
                <MoreIconWrapper subsection={subsection} />
            </MoreIconButton>

            {state.isDropdownShown ? (
                <ActionsDropdownWrapper
                    height={state.dropdownHeight}
                    inset={state.dropdownInset}
                >
                    {onEdit === undefined ? null : <Action onClick={onEdit}>Edit</Action>}
                    {onDuplicate === undefined ? null : <Action onClick={onDuplicate}>Duplicate</Action>}
                    {noDeletePopup ? (
                        <Action onClick={onDelete}>Delete</Action>
                    ) : (
                        <Action
                            onClick={handleShowDeletePopup}
                            data-test='delete-button'
                        >
                            Delete
                        </Action>
                    )}
                </ActionsDropdownWrapper>
            ) : null}

            {showDeletePopup === false || handleHideDeletePopup === undefined ? null : (
                <Popup
                    width={384}
                    handleHidePopup={handleHideDeletePopup}
                >
                    <DeletePopup
                        handleDelete={onDelete}
                        handleHidePopup={handleHideDeletePopup}
                    />
                </Popup>
            )}
        </MoreActionsWrapper>
    );
});
